import { Injectable } from '@angular/core';
import {
  ParkingDashboardDailyPoint,
  ParkingDashboardKpis,
  ParkingDashboardPeaks,
  ParkingDashboardResult,
  ParkingDashboardRevenueByType,
  ParkingDashboardSiteFlags,
  ParkingDashboardStatCard,
  ParkingDashboardTrafficByVehicleType,
  ParkingFinanceDashboardResult,
  ParkingFinanceDailyPoint,
  ParkingFinancePeaks,
  ParkingOpsDashboardResult,
  ParkingOpsDailyPoint,
  ParkingOpsPeaks,
  ParkingOpsEntryByGate,
} from '../../models/report/parking-dashboard.model';

@Injectable({ providedIn: 'root' })
export class MapperParkingDashboardService {
  private readonly typeIcons: Record<number, { icon: string; color: string }> = {
    1: { icon: 'local_parking', color: 'text-amber-600' },
    2: { icon: 'badge', color: 'text-orange-600' },
    3: { icon: 'sell', color: 'text-pink-600' },
    4: { icon: 'loyalty', color: 'text-rose-600' },
    5: { icon: 'confirmation_number', color: 'text-fuchsia-600' },
    6: { icon: 'credit_card_off', color: 'text-red-600' },
    7: { icon: 'ev_station', color: 'text-teal-600' },
  };

  map(res: unknown): ParkingDashboardResult {
    const raw = (res ?? {}) as Record<string, unknown>;
    const flagsRaw = (raw['flags'] ?? raw['Flags'] ?? {}) as Record<string, unknown>;
    const kpisRaw = (raw['kpis'] ?? raw['Kpis'] ?? {}) as Record<string, unknown>;
    const peaksRaw = (raw['peaks'] ?? raw['Peaks'] ?? {}) as Record<string, unknown>;
    const dailyRaw = (raw['daily'] ?? raw['Daily'] ?? []) as unknown[];
    const byTypeRaw = (raw['revenueByType'] ?? raw['RevenueByType'] ?? []) as unknown[];
    const trafficRaw = (raw['trafficByVehicleType'] ?? raw['TrafficByVehicleType'] ?? []) as unknown[];

    return {
      siteName: String(raw['siteName'] ?? raw['SiteName'] ?? ''),
      startDate: (raw['startDate'] ?? raw['StartDate'] ?? null) as string | null,
      endDate: (raw['endDate'] ?? raw['EndDate'] ?? null) as string | null,
      flags: this.mapFlags(flagsRaw),
      kpis: this.mapKpis(kpisRaw),
      trafficByVehicleType: trafficRaw.map((d) => this.mapTraffic(d)),
      revenueByType: byTypeRaw.map((d) => this.mapByType(d)),
      daily: dailyRaw.map((d) => this.mapDaily(d)),
      peaks: this.mapPeaks(peaksRaw),
    };
  }

  mapOps(res: unknown): ParkingOpsDashboardResult {
    const raw = (res ?? {}) as Record<string, unknown>;
    const flagsRaw = (raw['flags'] ?? raw['Flags'] ?? {}) as Record<string, unknown>;
    const kpisRaw = (raw['kpis'] ?? raw['Kpis'] ?? {}) as Record<string, unknown>;
    const peaksRaw = (raw['peaks'] ?? raw['Peaks'] ?? {}) as Record<string, unknown>;
    const dailyRaw = (raw['daily'] ?? raw['Daily'] ?? []) as unknown[];
    const trafficRaw = (raw['trafficByVehicleType'] ?? raw['TrafficByVehicleType'] ?? []) as unknown[];
    const entryByGateRaw = (raw['entryByGate'] ?? raw['EntryByGate'] ?? []) as unknown[];

    return {
      siteName: String(raw['siteName'] ?? raw['SiteName'] ?? ''),
      startDate: (raw['startDate'] ?? raw['StartDate'] ?? null) as string | null,
      endDate: (raw['endDate'] ?? raw['EndDate'] ?? null) as string | null,
      flags: this.mapFlags(flagsRaw),
      kpis: {
        entryCount: Number(kpisRaw['entryCount'] ?? kpisRaw['EntryCount'] ?? 0),
        exitCount: Number(kpisRaw['exitCount'] ?? kpisRaw['ExitCount'] ?? 0),
        evEntryCount: Number(kpisRaw['evEntryCount'] ?? kpisRaw['EvEntryCount'] ?? 0),
        evExitCount: Number(kpisRaw['evExitCount'] ?? kpisRaw['EvExitCount'] ?? 0),
      },
      trafficByVehicleType: trafficRaw.map((d) => this.mapTraffic(d)),
      entryByGate: entryByGateRaw.map((d) => this.mapEntryByGate(d)),
      daily: dailyRaw.map((d) => this.mapOpsDaily(d)),
      peaks: this.mapOpsPeaks(peaksRaw),
    };
  }

  mapFinance(res: unknown): ParkingFinanceDashboardResult {
    const raw = (res ?? {}) as Record<string, unknown>;
    const flagsRaw = (raw['flags'] ?? raw['Flags'] ?? {}) as Record<string, unknown>;
    const kpisRaw = (raw['kpis'] ?? raw['Kpis'] ?? {}) as Record<string, unknown>;
    const peaksRaw = (raw['peaks'] ?? raw['Peaks'] ?? {}) as Record<string, unknown>;
    const dailyRaw = (raw['daily'] ?? raw['Daily'] ?? []) as unknown[];
    const byTypeRaw = (raw['revenueByType'] ?? raw['RevenueByType'] ?? []) as unknown[];
    const target = kpisRaw['targetTotalRevenue'] ?? kpisRaw['TargetTotalRevenue'];

    return {
      siteName: String(raw['siteName'] ?? raw['SiteName'] ?? ''),
      startDate: (raw['startDate'] ?? raw['StartDate'] ?? null) as string | null,
      endDate: (raw['endDate'] ?? raw['EndDate'] ?? null) as string | null,
      flags: this.mapFlags(flagsRaw),
      kpis: {
        receiptCount: Number(kpisRaw['receiptCount'] ?? kpisRaw['ReceiptCount'] ?? 0),
        totalRevenue: Number(kpisRaw['totalRevenue'] ?? kpisRaw['TotalRevenue'] ?? 0),
        cashAmount: Number(kpisRaw['cashAmount'] ?? kpisRaw['CashAmount'] ?? 0),
        qrAmount: Number(kpisRaw['qrAmount'] ?? kpisRaw['QrAmount'] ?? 0),
        transferAmount: Number(kpisRaw['transferAmount'] ?? kpisRaw['TransferAmount'] ?? 0),
        targetTotalRevenue: target == null || target === '' ? null : Number(target),
      },
      revenueByType: byTypeRaw.map((d) => this.mapByType(d)),
      daily: dailyRaw.map((d) => this.mapFinanceDaily(d)),
      peaks: this.mapFinancePeaks(peaksRaw),
    };
  }

  toStatCards(result: ParkingDashboardResult): ParkingDashboardStatCard[] {
    const { kpis, revenueByType, trafficByVehicleType, flags } = result;
    const cards: ParkingDashboardStatCard[] = [
      this.card('entry', 'จำนวนเข้า (จอด)', this.n0(kpis.entryCount), 'login', 'text-sky-600'),
      this.card('exit', 'จำนวนออก (จอด)', this.n0(kpis.exitCount), 'logout', 'text-indigo-600'),
      this.card('receipts', 'ใบเสร็จทั้งหมด', this.n0(kpis.receiptCount), 'receipt_long', 'text-slate-600'),
      this.card('total', 'รายได้รวม', this.n2(kpis.totalRevenue), 'payments', 'text-amber-600', 'บาท'),
      this.card('cash', 'เงินสด', this.n2(kpis.cashAmount), 'payments', 'text-emerald-600', 'บาท'),
      this.card('qr', 'QR', this.n2(kpis.qrAmount), 'qr_code_2', 'text-violet-600', 'บาท'),
      this.card('transfer', 'โอนเงิน', this.n2(kpis.transferAmount), 'account_balance', 'text-cyan-600', 'บาท'),
    ];

    if (flags.ev) {
      cards.splice(
        2,
        0,
        this.card('ev-in', 'EV เข้าใช้งาน', this.n0(kpis.evEntryCount), 'ev_station', 'text-teal-600'),
        this.card('ev-out', 'EV ออก', this.n0(kpis.evExitCount), 'ev_station', 'text-teal-700'),
      );
    }

    for (const v of trafficByVehicleType) {
      if (v.vehicleTypeId < 0) continue;
      cards.push(
        this.card(`vt-in-${v.vehicleTypeId}`, `เข้า · ${v.vehicleTypeName}`, this.n0(v.entryCount), 'directions_car', 'text-sky-600'),
        this.card(`vt-out-${v.vehicleTypeId}`, `ออก · ${v.vehicleTypeName}`, this.n0(v.exitCount), 'directions_car', 'text-indigo-600'),
      );
    }

    for (const t of revenueByType) {
      const style = this.typeIcons[t.typeFeeId] ?? { icon: 'category', color: 'text-slate-600' };
      cards.push(this.card(`type-${t.typeFeeId}`, t.typeFeeName, this.n2(t.amount), style.icon, style.color, 'บาท'));
    }

    return cards;
  }

  toOpsStatCards(result: ParkingOpsDashboardResult): ParkingDashboardStatCard[] {
    const { kpis, trafficByVehicleType, flags } = result;
    const cards: ParkingDashboardStatCard[] = [
      this.card('entry', 'จำนวนเข้า (จอด)', this.n0(kpis.entryCount), 'login', 'text-sky-600'),
      this.card('exit', 'จำนวนออก (จอด)', this.n0(kpis.exitCount), 'logout', 'text-indigo-600'),
    ];

    if (flags.ev) {
      cards.push(
        this.card('ev-in', 'EV เข้าใช้งาน', this.n0(kpis.evEntryCount), 'ev_station', 'text-teal-600'),
        this.card('ev-out', 'EV ออก', this.n0(kpis.evExitCount), 'ev_station', 'text-teal-700'),
      );
    }

    for (const v of trafficByVehicleType) {
      if (v.vehicleTypeId < 0) continue;
      cards.push(
        this.card(`vt-in-${v.vehicleTypeId}`, `เข้า · ${v.vehicleTypeName}`, this.n0(v.entryCount), 'directions_car', 'text-sky-600'),
        this.card(`vt-out-${v.vehicleTypeId}`, `ออก · ${v.vehicleTypeName}`, this.n0(v.exitCount), 'directions_car', 'text-indigo-600'),
      );
    }

    return cards;
  }

  toFinanceStatCards(result: ParkingFinanceDashboardResult): ParkingDashboardStatCard[] {
    const { kpis, revenueByType } = result;
    const cards: ParkingDashboardStatCard[] = [
      this.card('receipts', 'ใบเสร็จทั้งหมด', this.n0(kpis.receiptCount), 'receipt_long', 'text-slate-600'),
      this.card('total', 'รายได้รวม', this.n2(kpis.totalRevenue), 'payments', 'text-amber-600', 'บาท'),
      this.card('cash', 'เงินสด', this.n2(kpis.cashAmount), 'payments', 'text-emerald-600', 'บาท'),
      this.card('qr', 'QR', this.n2(kpis.qrAmount), 'qr_code_2', 'text-violet-600', 'บาท'),
      this.card('transfer', 'โอนเงิน', this.n2(kpis.transferAmount), 'account_balance', 'text-cyan-600', 'บาท'),
    ];

    for (const t of revenueByType) {
      const style = this.typeIcons[t.typeFeeId] ?? { icon: 'category', color: 'text-slate-600' };
      cards.push(this.card(`type-${t.typeFeeId}`, t.typeFeeName, this.n2(t.amount), style.icon, style.color, 'บาท'));
    }

    return cards;
  }

  private mapFlags(raw: Record<string, unknown>): ParkingDashboardSiteFlags {
    const parking = raw['parking'] ?? raw['Parking'];
    return {
      parking: parking === undefined || parking === null ? true : Boolean(parking),
      ev: Boolean(raw['ev'] ?? raw['Ev'] ?? false),
      monthly: Boolean(raw['monthly'] ?? raw['Monthly'] ?? false),
    };
  }

  private mapKpis(raw: Record<string, unknown>): ParkingDashboardKpis {
    const target = raw['targetTotalRevenue'] ?? raw['TargetTotalRevenue'];
    return {
      entryCount: Number(raw['entryCount'] ?? raw['EntryCount'] ?? 0),
      exitCount: Number(raw['exitCount'] ?? raw['ExitCount'] ?? 0),
      receiptCount: Number(raw['receiptCount'] ?? raw['ReceiptCount'] ?? 0),
      totalRevenue: Number(raw['totalRevenue'] ?? raw['TotalRevenue'] ?? 0),
      cashAmount: Number(raw['cashAmount'] ?? raw['CashAmount'] ?? 0),
      qrAmount: Number(raw['qrAmount'] ?? raw['QrAmount'] ?? 0),
      transferAmount: Number(raw['transferAmount'] ?? raw['TransferAmount'] ?? 0),
      evEntryCount: Number(raw['evEntryCount'] ?? raw['EvEntryCount'] ?? 0),
      evExitCount: Number(raw['evExitCount'] ?? raw['EvExitCount'] ?? 0),
      targetTotalRevenue: target == null || target === '' ? null : Number(target),
    };
  }

  private mapByType(dto: unknown): ParkingDashboardRevenueByType {
    const d = (dto ?? {}) as Record<string, unknown>;
    return {
      typeFeeId: Number(d['typeFeeId'] ?? d['TypeFeeId'] ?? 0),
      typeFeeName: String(d['typeFeeName'] ?? d['TypeFeeName'] ?? ''),
      count: Number(d['count'] ?? d['Count'] ?? 0),
      amount: Number(d['amount'] ?? d['Amount'] ?? 0),
    };
  }

  private mapTraffic(dto: unknown): ParkingDashboardTrafficByVehicleType {
    const d = (dto ?? {}) as Record<string, unknown>;
    return {
      vehicleTypeId: Number(d['vehicleTypeId'] ?? d['VehicleTypeId'] ?? 0),
      vehicleTypeName: String(d['vehicleTypeName'] ?? d['VehicleTypeName'] ?? ''),
      entryCount: Number(d['entryCount'] ?? d['EntryCount'] ?? 0),
      exitCount: Number(d['exitCount'] ?? d['ExitCount'] ?? 0),
    };
  }

  private mapDaily(dto: unknown): ParkingDashboardDailyPoint {
    const d = (dto ?? {}) as Record<string, unknown>;
    return {
      date: (d['date'] ?? d['Date'] ?? null) as string | null,
      entryCount: Number(d['entryCount'] ?? d['EntryCount'] ?? 0),
      exitCount: Number(d['exitCount'] ?? d['ExitCount'] ?? 0),
      evEntryCount: Number(d['evEntryCount'] ?? d['EvEntryCount'] ?? 0),
      evExitCount: Number(d['evExitCount'] ?? d['EvExitCount'] ?? 0),
      totalRevenue: Number(d['totalRevenue'] ?? d['TotalRevenue'] ?? d['parkingRevenue'] ?? d['ParkingRevenue'] ?? 0),
      parkingRevenue: Number(d['parkingRevenue'] ?? d['ParkingRevenue'] ?? 0),
      memberRevenue: Number(d['memberRevenue'] ?? d['MemberRevenue'] ?? 0),
      evRevenue: Number(d['evRevenue'] ?? d['EvRevenue'] ?? 0),
    };
  }

  private mapOpsDaily(dto: unknown): ParkingOpsDailyPoint {
    const d = (dto ?? {}) as Record<string, unknown>;
    return {
      date: (d['date'] ?? d['Date'] ?? null) as string | null,
      entryCount: Number(d['entryCount'] ?? d['EntryCount'] ?? 0),
      exitCount: Number(d['exitCount'] ?? d['ExitCount'] ?? 0),
      evEntryCount: Number(d['evEntryCount'] ?? d['EvEntryCount'] ?? 0),
      evExitCount: Number(d['evExitCount'] ?? d['EvExitCount'] ?? 0),
    };
  }

  private mapFinanceDaily(dto: unknown): ParkingFinanceDailyPoint {
    const d = (dto ?? {}) as Record<string, unknown>;
    return {
      date: (d['date'] ?? d['Date'] ?? null) as string | null,
      totalRevenue: Number(d['totalRevenue'] ?? d['TotalRevenue'] ?? 0),
      parkingRevenue: Number(d['parkingRevenue'] ?? d['ParkingRevenue'] ?? 0),
      memberRevenue: Number(d['memberRevenue'] ?? d['MemberRevenue'] ?? 0),
      evRevenue: Number(d['evRevenue'] ?? d['EvRevenue'] ?? 0),
    };
  }

  private mapPeaks(raw: Record<string, unknown>): ParkingDashboardPeaks {
    return {
      peakEntryDate: (raw['peakEntryDate'] ?? raw['PeakEntryDate'] ?? null) as string | null,
      peakEntryCount: Number(raw['peakEntryCount'] ?? raw['PeakEntryCount'] ?? 0),
      peakExitDate: (raw['peakExitDate'] ?? raw['PeakExitDate'] ?? null) as string | null,
      peakExitCount: Number(raw['peakExitCount'] ?? raw['PeakExitCount'] ?? 0),
      peakRevenueDate: (raw['peakRevenueDate'] ?? raw['PeakRevenueDate'] ?? null) as string | null,
      peakRevenueAmount: Number(raw['peakRevenueAmount'] ?? raw['PeakRevenueAmount'] ?? 0),
    };
  }

  private mapOpsPeaks(raw: Record<string, unknown>): ParkingOpsPeaks {
    return {
      peakEntryDate: (raw['peakEntryDate'] ?? raw['PeakEntryDate'] ?? null) as string | null,
      peakEntryCount: Number(raw['peakEntryCount'] ?? raw['PeakEntryCount'] ?? 0),
      peakExitDate: (raw['peakExitDate'] ?? raw['PeakExitDate'] ?? null) as string | null,
      peakExitCount: Number(raw['peakExitCount'] ?? raw['PeakExitCount'] ?? 0),
    };
  }

  private mapEntryByGate(dto: unknown): ParkingOpsEntryByGate {
    const d = (dto ?? {}) as Record<string, unknown>;
    return {
      gateId: Number(d['gateId'] ?? d['GateId'] ?? 0),
      gateName: String(d['gateName'] ?? d['GateName'] ?? ''),
      entryCount: Number(d['entryCount'] ?? d['EntryCount'] ?? 0),
    };
  }

  private mapFinancePeaks(raw: Record<string, unknown>): ParkingFinancePeaks {
    return {
      peakRevenueDate: (raw['peakRevenueDate'] ?? raw['PeakRevenueDate'] ?? null) as string | null,
      peakRevenueAmount: Number(raw['peakRevenueAmount'] ?? raw['PeakRevenueAmount'] ?? 0),
    };
  }

  private card(
    key: string,
    title: string,
    value: string,
    icon: string,
    color: string,
    hint?: string,
  ): ParkingDashboardStatCard {
    return { key, title, value, icon, color, hint };
  }

  private n0(v: number): string {
    return new Intl.NumberFormat('th-TH', { maximumFractionDigits: 0 }).format(v || 0);
  }

  private n2(v: number): string {
    return new Intl.NumberFormat('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(v || 0);
  }
}
